
import { Component, ChangeDetectorRef, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

interface User {
  name: string;
  src: string;
  visible: boolean;
  status: string;
  userId?: string;
  deviceId?: string;
}

@Component({
  selector: 'app-glb-floor-view',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './glb-floor-view.component.html',
  styleUrl: './glb-floor-view.component.css',
})
export class GlbFloorViewComponent implements OnChanges {
  @Input() floorMapEntity: any = {};
  @Input() liveData: any;
  @Input() blenderLiveUserData: any;
  @Input() assetResolutionKey: string | null = null;
  @Input() alertRecords: any[] = [];
  @Input() blenderViewLoading = false;
  @Input() blenderViewEnabled = false;
  @Input() fullUsers: User[] = [];
  @Input() sidebarLeafletView: any = null;
  @Input() data: any = {};
  @Input() perimeters: any[] = [];
  @Input() showBeacons = false;

  @Output() close = new EventEmitter<void>();
  @Output() userClicked = new EventEmitter<{ userId: string; roomId: string; mesh: any }>();
  @Output() userSidebarClicked = new EventEmitter<User>();
  @Output() toggleUserVisibility = new EventEmitter<User>();
  @Output() loadingChange = new EventEmitter<boolean>();

  selectedRoomId: string | null = null;
  selectedRoomStatusFilter: 'all' | 'danger' | 'safe' | 'unknown' | null = null;
  showRoomsList = false;

  private roomAliasToCanonical = new Map<string, string>();
  private roomCatalog = new Map<string, { id: string; name: string }>();
  private roomIndexDirty = true;
  private localUserVisibilityMap = new Map<string, boolean>();
  selectedRoomUserCols: User[][] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['floorMapEntity'] || changes['perimeters'] || changes['blenderLiveUserData']) {
      this.markRoomIndexDirty();
    }
    if (changes['blenderLiveUserData'] || changes['fullUsers']) {
      this.updateSelectedRoomUsers();
    }
  }

  toggleRoomsList(): void {
    this.showRoomsList = !this.showRoomsList;
    this.cdr.detectChanges();
  }

  get currentFloorOrRoomStats() {
    const danger = this.data.dangerCount || 0;
    const safe = this.data.safeCount || 0;
    const unknown = this.data.unknownCount || 0;
    const suspect = this.data.suspectCount || 0;

    const total = danger + safe + unknown + suspect;
    return { danger, safe, unknown, suspect, total };
  }

  getDashArray(count: number, total: number): string {
    if (total === 0) return '0 100';
    const pct = (count / total) * 100;
    return `${pct} ${100 - pct}`;
  }

  getDashOffset(prevCount: number, total: number): number {
    if (total === 0) return 0;
    const prevPct = (prevCount / total) * 100;
    return 100 - prevPct + 25;
  }

  getRoomsOnFloor() {
    this.rebuildRoomIndexIfNeeded();

    const seen = new Set<string>();
    const rooms: { id: string; name: string }[] = [];

    for (const room of this.roomCatalog.values()) {
      if (!seen.has(room.id)) {
        seen.add(room.id);
        rooms.push({ ...room });
      }
    }

    if (this.blenderLiveUserData?.byRoom) {
      Object.keys(this.blenderLiveUserData.byRoom).forEach((rawRoomId) => {
        const canonicalId = this.resolveRoomId(rawRoomId);
        if (!seen.has(canonicalId)) {
          seen.add(canonicalId);
          const catalogRoom = this.roomCatalog.get(canonicalId);
          rooms.push(catalogRoom ?? { id: canonicalId, name: `Room ${rawRoomId}` });
        }
      });
    }

    return rooms.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  markRoomIndexDirty(): void {
    this.roomIndexDirty = true;
  }

  private rebuildRoomIndexIfNeeded(): void {
    if (!this.roomIndexDirty) {
      return;
    }

    this.roomAliasToCanonical.clear();
    this.roomCatalog.clear();

    const perimeterSources: any[] = [...(this.perimeters || [])];
    const floorPerimeters = this.floorMapEntity?.perimeters;
    if (floorPerimeters) {
      perimeterSources.push(...Object.values(floorPerimeters));
    }

    perimeterSources.forEach((perimeter) => {
      if (!perimeter || perimeter.perimeterType === 'FLOOR' || perimeter.type === 'FLOOR') {
        return;
      }
      this.registerRoomPerimeter(perimeter);
    });

    this.roomIndexDirty = false;
  }

  private registerRoomPerimeter(perimeter: any): void {
    const canonicalId = String(perimeter.perimeterId ?? perimeter.arcAngelId ?? perimeter.id ?? '').trim();
    if (!canonicalId) {
      return;
    }

    const name = this.getRoomDisplayName(perimeter, canonicalId);
    this.roomCatalog.set(canonicalId, { id: canonicalId, name });

    [perimeter.id, perimeter.perimeterId, perimeter.arcAngelId, perimeter.label, perimeter.name]
      .filter((value) => value !== undefined && value !== null && value !== '')
      .forEach((alias) => this.registerRoomAlias(alias, canonicalId));

    const label = String(perimeter.label ?? perimeter.name ?? '').trim();
    const roomNumMatch = label.match(/ROOM[_\s-]*(\d+)/i);
    if (roomNumMatch) {
      const roomNumber = roomNumMatch[1];
      this.registerRoomAlias(roomNumber, canonicalId);
      this.registerRoomAlias(`room_${roomNumber}`, canonicalId);
      this.registerRoomAlias(`ROOM_${roomNumber}`, canonicalId);
    }
  }

  private registerRoomAlias(alias: string | number, canonicalId: string): void {
    const raw = String(alias ?? '').trim();
    if (!raw) {
      return;
    }
    this.roomAliasToCanonical.set(raw, canonicalId);
    this.roomAliasToCanonical.set(raw.toUpperCase(), canonicalId);
    this.roomAliasToCanonical.set(raw.toLowerCase(), canonicalId);
  }

  private getRoomDisplayName(perimeter: any, fallbackId: string): string {
    const name =
      perimeter?.label ||
      perimeter?.name ||
      (typeof perimeter?.getName === 'function' ? perimeter.getName() : '');
    if (name) {
      return String(name).trim();
    }
    return `Room ${fallbackId}`;
  }

  resolveRoomId(roomId: string): string {
    this.rebuildRoomIndexIfNeeded();
    const raw = String(roomId ?? '').trim();
    if (!raw) {
      return raw;
    }
    return (
      this.roomAliasToCanonical.get(raw) ??
      this.roomAliasToCanonical.get(raw.toUpperCase()) ??
      this.roomAliasToCanonical.get(raw.toLowerCase()) ??
      raw
    );
  }

  private getRoomAliasIds(canonicalId: string): string[] {
    this.rebuildRoomIndexIfNeeded();
    const ids = new Set<string>([canonicalId]);

    this.roomAliasToCanonical.forEach((resolvedId, alias) => {
      if (resolvedId === canonicalId) {
        ids.add(alias);
      }
    });

    if (this.blenderLiveUserData?.byRoom) {
      Object.keys(this.blenderLiveUserData.byRoom).forEach((rawId) => {
        if (this.resolveRoomId(rawId) === canonicalId) {
          ids.add(rawId);
        }
      });
    }

    return Array.from(ids);
  }

  getRoomStatusCounts(roomId: string): { danger: number; safe: number; unknown: number } {
    const canonicalId = this.resolveRoomId(roomId);
    const aliasIds = this.getRoomAliasIds(canonicalId);
    let danger = 0;
    let safe = 0;
    let unknown = 0;

    aliasIds.forEach((id) => {
      const roomStatus = this.blenderLiveUserData?.roomStatusByRoomId?.[id];
      if (!roomStatus) {
        return;
      }
      danger += roomStatus.danger ?? 0;
      safe += roomStatus.safe ?? 0;
      unknown += roomStatus.unknown ?? 0;
    });

    return { danger, safe, unknown };
  }

  getUsersForRoom(roomId: string): User[] {
    if (!this.blenderLiveUserData?.byRoom) {
      return [];
    }

    const canonicalId = this.resolveRoomId(roomId);
    const aliasIds = this.getRoomAliasIds(canonicalId);
    const userIds = new Set<string>();

    aliasIds.forEach((id) => {
      const roomRecords = this.blenderLiveUserData.byRoom[id] || [];
      roomRecords.forEach((record: any) => {
        if (record?.userId != null) {
          userIds.add(String(record.userId));
        }
      });
    });

    if (userIds.size === 0) {
      return [];
    }

    let users = this.fullUsers
      .filter((user) => user.userId && userIds.has(String(user.userId)))
      .map((user) => {
        const userIdStr = String(user.userId);
        if (!this.localUserVisibilityMap.has(userIdStr)) {
          this.localUserVisibilityMap.set(userIdStr, user.visible ?? true);
        }
        return {
          ...user,
          visible: this.localUserVisibilityMap.get(userIdStr) ?? true
        };
      });

    const filter = this.selectedRoomStatusFilter;
    if (filter && filter !== 'all') {
      const statusMap: Record<string, string> = {
        danger: 'DANGER',
        safe: 'SAFE',
        unknown: 'UNKNOWN',
      };
      const targetStatus = statusMap[filter];
      users = users.filter((user) => user.status === targetStatus);
    }

    return users;
  }

  getRoomUserColumns(roomId: string): User[][] {
    const users = this.getUsersForRoom(roomId);
    const col1End = Math.ceil(users.length / 3);
    const col2End = Math.ceil((users.length * 2) / 3);
    return [
      users.slice(0, col1End),
      users.slice(col1End, col2End),
      users.slice(col2End),
    ];
  }

  getSelectedRoomName(): string {
    if (!this.selectedRoomId) return '';
    const canonicalId = this.resolveRoomId(this.selectedRoomId);
    const catalogRoom = this.roomCatalog.get(canonicalId);
    if (catalogRoom) {
      return catalogRoom.name;
    }
    const rooms = this.getRoomsOnFloor();
    const found = rooms.find((room) => room.id === canonicalId);
    return found ? found.name : `Room ${this.selectedRoomId}`;
  }

  onBlenderRoomClicked(event: { roomId: string; floorId: string; mesh: any }) {
    if (event && event.roomId) {
      const resolvedRoomId = this.resolveRoomId(event.roomId);
      if (this.selectedRoomId === resolvedRoomId) {
        this.selectedRoomId = null;
        this.selectedRoomStatusFilter = null;
      } else {
        this.selectRoomAll(resolvedRoomId);
      }
      this.showRoomsList = true;
      this.cdr.detectChanges();
    }
  }

  selectRoomAll(roomId: string): void {
    const resolved = this.resolveRoomId(roomId);
    if (this.selectedRoomId === resolved && this.selectedRoomStatusFilter === 'all') {
      this.selectedRoomId = null;
      this.selectedRoomStatusFilter = null;
    } else {
      this.selectedRoomId = resolved;
      this.selectedRoomStatusFilter = 'all';
    }
    this.updateSelectedRoomUsers();
    this.showRoomsList = true;
    this.cdr.detectChanges();
  }

  selectRoomStatus(roomId: string, status: 'danger' | 'safe' | 'unknown'): void {
    const resolved = this.resolveRoomId(roomId);
    if (this.selectedRoomId === resolved && this.selectedRoomStatusFilter === status) {
      this.selectedRoomId = null;
      this.selectedRoomStatusFilter = null;
    } else {
      this.selectedRoomId = resolved;
      this.selectedRoomStatusFilter = status;
    }
    this.updateSelectedRoomUsers();
    this.showRoomsList = true;
    this.cdr.detectChanges();
  }

  selectRoom(roomId: string) {
    this.selectRoomAll(roomId);
  }

  showUserInfo(user: User) {
    this.userSidebarClicked.emit({ ...user, action: 'open' } as any);
  }

  toggleLocalUserVisibility(user: User) {
    const userIdStr = String(user.userId);
    const current = this.localUserVisibilityMap.get(userIdStr) ?? true;
    const newVisible = !current;
    this.localUserVisibilityMap.set(userIdStr, newVisible);
    this.updateSelectedRoomUsers();
    this.cdr.detectChanges();
    this.userSidebarClicked.emit({ ...user, visible: newVisible, action: 'toggle' } as any);
  }

  trackByUserId(index: number, user: User): string | undefined {
    return user.userId;
  }

  updateSelectedRoomUsers(): void {
    if (!this.selectedRoomId) {
      this.selectedRoomUserCols = [];
      return;
    }

    const users = this.getUsersForRoom(this.selectedRoomId);
    if (users.length === 0) {
      this.selectedRoomUserCols = [];
      return;
    }

    const col1End = Math.ceil(users.length / 3);
    const col2End = Math.ceil((users.length * 2) / 3);
    this.selectedRoomUserCols = [
      users.slice(0, col1End),
      users.slice(col1End, col2End),
      users.slice(col2End),
    ];
  }

  get hasSelectedRoomUsers(): boolean {
    return this.selectedRoomUserCols.some(col => col && col.length > 0);
  }
}
